import { Component } from 'react';
import { CssGeneratorLayout, cssGenCommon as genStyles } from '../converter-layout/css-generator-layout';

type Direction = 'up' | 'down' | 'left' | 'right' | 'up-right' | 'up-left' | 'down-right' | 'down-left';

interface State {
    direction: Direction;
    size: number;
    color: string;
    copied: boolean;
}

function trianglecss(dir: Direction, size: number, color: string): string {
    const tp = 'transparent';
    const s = `${size}px`;
    switch (dir) {
        case 'up':
            return `width: 0;\nheight: 0;\nborder-left: ${s} solid ${tp};\nborder-right: ${s} solid ${tp};\nborder-bottom: ${s} solid ${color};`;
        case 'down':
            return `width: 0;\nheight: 0;\nborder-left: ${s} solid ${tp};\nborder-right: ${s} solid ${tp};\nborder-top: ${s} solid ${color};`;
        case 'left':
            return `width: 0;\nheight: 0;\nborder-top: ${s} solid ${tp};\nborder-bottom: ${s} solid ${tp};\nborder-right: ${s} solid ${color};`;
        case 'right':
            return `width: 0;\nheight: 0;\nborder-top: ${s} solid ${tp};\nborder-bottom: ${s} solid ${tp};\nborder-left: ${s} solid ${color};`;
        case 'up-right':
            return `width: 0;\nheight: 0;\nborder-top: ${parseInt(s) * 2}px solid ${color};\nborder-left: ${parseInt(s) * 2}px solid ${tp};`;
        case 'up-left':
            return `width: 0;\nheight: 0;\nborder-top: ${parseInt(s) * 2}px solid ${color};\nborder-right: ${parseInt(s) * 2}px solid ${tp};`;
        case 'down-right':
            return `width: 0;\nheight: 0;\nborder-bottom: ${parseInt(s) * 2}px solid ${color};\nborder-left: ${parseInt(s) * 2}px solid ${tp};`;
        case 'down-left':
            return `width: 0;\nheight: 0;\nborder-bottom: ${parseInt(s) * 2}px solid ${color};\nborder-right: ${parseInt(s) * 2}px solid ${tp};`;
    }
}

export default class TriangleGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        direction: 'up',
        size: 50,
        color: '#6366f1',
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private get cssCode(): string {
        return trianglecss(this.state.direction, this.state.size, this.state.color);
    }

    copyCss = () => {
        navigator.clipboard.writeText(this.cssCode).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    private previewStyle(): React.CSSProperties {
        const { direction, size, color } = this.state;
        const t = 'transparent';
        const s: React.CSSProperties = { width: 0, height: 0 };
        switch (direction) {
            case 'up': s.borderLeft = `${size}px solid ${t}`; s.borderRight = `${size}px solid ${t}`; s.borderBottom = `${size}px solid ${color}`; break;
            case 'down': s.borderLeft = `${size}px solid ${t}`; s.borderRight = `${size}px solid ${t}`; s.borderTop = `${size}px solid ${color}`; break;
            case 'left': s.borderTop = `${size}px solid ${t}`; s.borderBottom = `${size}px solid ${t}`; s.borderRight = `${size}px solid ${color}`; break;
            case 'right': s.borderTop = `${size}px solid ${t}`; s.borderBottom = `${size}px solid ${t}`; s.borderLeft = `${size}px solid ${color}`; break;
            case 'up-right': s.borderTop = `${size * 2}px solid ${color}`; s.borderLeft = `${size * 2}px solid ${t}`; break;
            case 'up-left': s.borderTop = `${size * 2}px solid ${color}`; s.borderRight = `${size * 2}px solid ${t}`; break;
            case 'down-right': s.borderBottom = `${size * 2}px solid ${color}`; s.borderLeft = `${size * 2}px solid ${t}`; break;
            case 'down-left': s.borderBottom = `${size * 2}px solid ${color}`; s.borderRight = `${size * 2}px solid ${t}`; break;
        }
        return s;
    }

    render() {
        const { direction, size, color, copied } = this.state;

        const controls = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Direction</span></div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.4rem' }}>
                        {(['up-left', 'up', 'up-right', 'left', 'right', 'down-left', 'down', 'down-right'] as Direction[]).map((d, i) => (
                            i !== 4 ? (
                                <button key={d}
                                    className={`${genStyles.iconBtn} ${direction === d ? genStyles.iconBtnPrimary : ''}`}
                                    onClick={() => this.setState({ direction: d })}>
                                    {d}
                                </button>
                            ) : <div key="spacer" />
                        ))}
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Size &amp; color</span></div>
                    <div className={genStyles.field}>
                        <label>Size: <strong>{size}px</strong></label>
                        <input type="range" min={5} max={200} value={size}
                            onChange={e => this.setState({ size: +e.target.value })} />
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="tri-color">Color</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input id="tri-color" type="color" value={color}
                                style={{ width: 44, height: 38, border: '1px solid #e2e8f0', borderRadius: 6 }}
                                onChange={e => this.setState({ color: e.target.value })} />
                            <input type="text" value={color}
                                style={{ flex: 1, padding: '0.55rem 0.75rem', borderRadius: 6, border: '1px solid #e2e8f0', fontFamily: 'ui-monospace, monospace', fontSize: '0.9rem' }}
                                onChange={e => this.setState({ color: e.target.value })} />
                        </div>
                    </div>
                </div>
            </>
        );

        return (
            <CssGeneratorLayout
                controls={controls}
                previewLabel="Preview"
                previewMeta={direction}
                preview={
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, width: '100%' }}>
                        <div style={this.previewStyle()} />
                    </div>
                }
                codeLabel="CSS"
                codeMeta={`${this.cssCode.length} chars`}
                code={this.cssCode}
                codeActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyCss}>
                        {copied ? '✓ Copied' : '📋 Copy CSS'}
                    </button>
                }
            />
        );
    }
}
